import * as Sentry from "@sentry/react";
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import { enableAnalytics } from "@/shared/integrations/firebase/analytics";
import './index.css'

// Customer app only — the internal QA app (src/qa/main.tsx) intentionally omits
// this so its usage never lands in the customer's GA4 property.
enableAnalytics();

const isProd = import.meta.env.VITE_APP_ENV === "production";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_APP_ENV ?? "unknown",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    // Capture failed HTTP requests at the transport layer, so handled errors
    // (supabase {data,error}, React Query, try/catch) surface too — not just
    // unhandled throws. Scoped to our own backends to avoid third-party noise.
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [[400, 599]],
      failedRequestTargets: [
        /supabase\.co/,
        /disco-outpost-268223/,
        /appspot\.com/,
      ],
    }),
  ],
  // Needed for httpClientIntegration to attach request/response headers + body
  // (so a failed request shows WHY it failed). Sends auth/cookies too — scrubbed
  // in beforeSend below.
  sendDefaultPii: true,
  // Sample down performance traces in prod to conserve Sentry quota; keep full
  // fidelity in test/dev.
  tracesSampleRate: isProd ? 0.2 : 1.0,
  replaysOnErrorSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  // Cap the breadcrumb trail — the Learning page fires 100+ paginated
  // property_assets fetches, whose long URLs bloat the error envelope past
  // Sentry's ingest size limit (413 Content Too Large, report dropped).
  maxBreadcrumbs: 50,
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
      const url = String(breadcrumb.data?.url ?? "");
      if (url.includes("/rest/v1/property_assets")) return null;
    }
    return breadcrumb;
  },
  beforeSend(event) {
    // Strip credentials that sendDefaultPii attaches to captured requests —
    // Firebase bearer tokens / cookies must never leave the browser.
    const headers = event.request?.headers;
    if (headers) {
      for (const key of Object.keys(headers)) {
        if (/^(authorization|cookie)$/i.test(key)) delete headers[key];
      }
    }
    // User-cancelled requests are not failures.
    const firstValue = event.exception?.values?.[0]?.value ?? "";
    if (firstValue.includes("AbortError")) return null;
    return event;
  },
});

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary
    fallback={
      <div style={{ padding: 24, textAlign: "center", fontFamily: "system-ui" }}>
        <p>Something went wrong. Please refresh the page.</p>
      </div>
    }
  >
    <App />
  </Sentry.ErrorBoundary>,
);
